import React, { useState } from 'react';
import { Input, Button, Modal, Spin, Checkbox, Tabs } from 'antd';
import {
  StepBackwardOutlined, CaretLeftOutlined,
  CaretRightOutlined, StepForwardOutlined,
  PlusOutlined, EditOutlined, DeleteOutlined,
  SaveOutlined, CloseOutlined, SearchOutlined,
} from '@ant-design/icons';
import { useCustomerForm } from './hooks/useCustomerForm';
import './CUSForm.css';

const { TextArea } = Input;

const CUSForm: React.FC = () => {
  const {
    mode, loading, currentRecord, updateField,
    navFirst, navPrev, navNext, navLast,
    startAdd, startEdit, cancel, save, doDelete,
    quickSearch, recordPosition, recordCount,
  } = useCustomerForm();

  const [searchKey, setSearchKey] = useState('');
  const isEditing = mode === 'add' || mode === 'edit';
  const r = currentRecord;

  const tabItems = [
    {
      key: 'basic',
      label: '基本資料',
      children: (
        <div className="cus-tab-content">
          <div className="cus-field-row">
            <label>客戶代號</label>
            <Input value={r.cus_code || ''} onChange={(e) => updateField('cus_code', e.target.value)}
              disabled={mode !== 'add'} style={{ width: 160 }} />
          </div>
          <div className="cus-field-row">
            <label>中文全名</label>
            <Input value={r.cus_cname || ''} onChange={(e) => updateField('cus_cname', e.target.value)}
              disabled={!isEditing} style={{ width: 300 }} />
          </div>
          <div className="cus-field-row">
            <label>英文全名</label>
            <Input value={r.cus_ename || ''} onChange={(e) => updateField('cus_ename', e.target.value)}
              disabled={!isEditing} style={{ width: 300 }} />
          </div>
          <div className="cus-field-row">
            <label>客戶類別</label>
            <Input value={r.comp_type || ''} onChange={(e) => updateField('comp_type', e.target.value)}
              disabled={!isEditing} style={{ width: 80 }} />
          </div>
          <div className="cus-field-row">
            <label>國碼</label>
            <Input value={r.country_code || ''} onChange={(e) => updateField('country_code', e.target.value)}
              disabled={!isEditing} style={{ width: 120 }} />
          </div>
          <div className="cus-field-row">
            <label>統編/稅編</label>
            <Input value={r.tax_code || ''} onChange={(e) => updateField('tax_code', e.target.value)}
              disabled={!isEditing} style={{ width: 160 }} />
          </div>
          <div className="cus-field-row">
            <label>電話</label>
            <Input value={r.phone || ''} onChange={(e) => updateField('phone', e.target.value)}
              disabled={!isEditing} style={{ width: 200 }} />
          </div>
          <div className="cus-field-row">
            <label>傳真</label>
            <Input value={r.comp_fax || ''} onChange={(e) => updateField('comp_fax', e.target.value)}
              disabled={!isEditing} style={{ width: 200 }} />
          </div>
          <div className="cus-field-row">
            <label>公司網址</label>
            <Input value={r.web || ''} onChange={(e) => updateField('web', e.target.value)}
              disabled={!isEditing} style={{ width: 300 }} />
          </div>
          <div className="cus-field-row">
            <label>公司郵件</label>
            <Input value={r.comp_mail || ''} onChange={(e) => updateField('comp_mail', e.target.value)}
              disabled={!isEditing} style={{ width: 300 }} />
          </div>
          <div className="cus-field-row">
            <label>公司地址</label>
            <Input value={r.address || ''} onChange={(e) => updateField('address', e.target.value)}
              disabled={!isEditing} style={{ width: 400 }} />
          </div>
        </div>
      ),
    },
    {
      key: 'business',
      label: '營業資料',
      children: (
        <div className="cus-tab-content">
          <div className="cus-field-row">
            <label>發票抬頭</label>
            <Input value={r.invoice || ''} onChange={(e) => updateField('invoice', e.target.value)}
              disabled={!isEditing} style={{ width: 300 }} />
          </div>
          <div className="cus-field-row">
            <label>發票地址</label>
            <Input value={r.invoic_address || ''} onChange={(e) => updateField('invoic_address', e.target.value)}
              disabled={!isEditing} style={{ width: 400 }} />
          </div>
          <div className="cus-field-row">
            <label>發票稅率</label>
            <Input type="number" value={r.tax_rate || 0} onChange={(e) => updateField('tax_rate', parseFloat(e.target.value) || 0)}
              disabled={!isEditing} style={{ width: 120 }} />
          </div>
          <div className="cus-field-row">
            <label>品牌</label>
            <Input value={r.brand || ''} onChange={(e) => updateField('brand', e.target.value)}
              disabled={!isEditing} style={{ width: 200 }} />
          </div>
          <div className="cus-field-row">
            <label>主要屬性</label>
            <Input value={r.main_prop || ''} onChange={(e) => updateField('main_prop', e.target.value)}
              disabled={!isEditing} style={{ width: 120 }} />
          </div>
          <div className="cus-field-row">
            <label>客戶屬性</label>
            <Input value={r.cus_prop || ''} onChange={(e) => updateField('cus_prop', e.target.value)}
              disabled={!isEditing} style={{ width: 300 }} placeholder="分號分隔，如 T1;T2;" />
          </div>
          <div className="cus-field-row">
            <label>受信額度</label>
            <Input type="number" value={r.credit_limit || 0} onChange={(e) => updateField('credit_limit', parseFloat(e.target.value) || 0)}
              disabled={!isEditing} style={{ width: 160 }} />
          </div>
          <div className="cus-field-row">
            <label>交易條件</label>
            <Input value={r.trade_terms || ''} onChange={(e) => updateField('trade_terms', e.target.value)}
              disabled={!isEditing} style={{ width: 160 }} />
          </div>
          <div className="cus-field-row">
            <label>備註說明</label>
            <TextArea value={r.explain || ''} onChange={(e) => updateField('explain', e.target.value)}
              disabled={!isEditing} rows={3} style={{ width: 400 }} />
          </div>
          <div className="cus-field-row">
            <Checkbox checked={r.nda || false} onChange={(e) => updateField('nda', e.target.checked)} disabled={!isEditing}>NDA 保密協議</Checkbox>
            <Checkbox checked={r.ispa || false} onChange={(e) => updateField('ispa', e.target.checked)} disabled={!isEditing}>ISPA 協議</Checkbox>
          </div>
          <div className="cus-field-row">
            <Checkbox checked={r.co_development || false} onChange={(e) => updateField('co_development', e.target.checked)} disabled={!isEditing}>共同開發</Checkbox>
            <Checkbox checked={r.co_branding || false} onChange={(e) => updateField('co_branding', e.target.checked)} disabled={!isEditing}>共同品牌</Checkbox>
          </div>
          <div className="cus-field-row">
            <Checkbox checked={r.is_active || false} onChange={(e) => updateField('is_active', e.target.checked)} disabled={!isEditing}>啟用</Checkbox>
            <Checkbox checked={r.ref_cus || false} disabled>為關聯方</Checkbox>
          </div>
        </div>
      ),
    },
    {
      key: 'audit',
      label: '系統資訊',
      children: (
        <div className="cus-tab-content">
          <div className="cus-field-row">
            <label>建立人員</label>
            <Input value={r.c_user || ''} disabled style={{ width: 120 }} />
          </div>
          <div className="cus-field-row">
            <label>建立人名稱</label>
            <Input value={r.user_name || ''} disabled style={{ width: 160 }} />
          </div>
          <div className="cus-field-row">
            <label>建立日期</label>
            <Input value={r.c_date ? String(r.c_date).substring(0, 10) : ''} disabled style={{ width: 140 }} />
          </div>
          <div className="cus-field-row">
            <label>更新人員</label>
            <Input value={r.u_user || ''} disabled style={{ width: 120 }} />
          </div>
          <div className="cus-field-row">
            <label>更新日期</label>
            <Input value={r.u_date ? String(r.u_date).substring(0, 10) : ''} disabled style={{ width: 140 }} />
          </div>
        </div>
      ),
    },
  ];

  return (
    <Spin spinning={loading}>
      <div className="cus-form">
        <h2>客戶檔案</h2>

        {/* Quick Search */}
        <div className="cus-search-bar">
          <Input
            placeholder="輸入代號或名稱搜尋"
            value={searchKey}
            onChange={(e) => setSearchKey(e.target.value)}
            onPressEnter={() => quickSearch(searchKey)}
            style={{ width: 200 }}
            disabled={isEditing}
          />
          <Button icon={<SearchOutlined />} onClick={() => quickSearch(searchKey)} disabled={isEditing}>搜尋</Button>
          <span className="cus-record-info">{recordPosition} / {recordCount}</span>
        </div>

        {/* Navigation */}
        <div className="cus-nav-bar">
          <Button icon={<StepBackwardOutlined />} onClick={navFirst} disabled={isEditing}>首筆</Button>
          <Button icon={<CaretLeftOutlined />} onClick={navPrev} disabled={isEditing}>前筆</Button>
          <Button icon={<CaretRightOutlined />} onClick={navNext} disabled={isEditing}>次筆</Button>
          <Button icon={<StepForwardOutlined />} onClick={navLast} disabled={isEditing}>末筆</Button>
        </div>

        {/* Tabs */}
        <Tabs items={tabItems} />

        {/* Action Buttons */}
        <div className="cus-actions">
          {!isEditing ? (
            <>
              <Button type="primary" icon={<PlusOutlined />} onClick={startAdd}>新增</Button>
              <Button icon={<EditOutlined />} onClick={startEdit} disabled={recordCount === 0}>修改</Button>
              <Button danger icon={<DeleteOutlined />} disabled={recordCount === 0}
                onClick={() => {
                  Modal.confirm({
                    title: '確認刪除',
                    content: `確定要刪除客戶 ${r.cus_code} - ${r.cus_cname} 嗎？`,
                    onOk: doDelete,
                  });
                }}>刪除</Button>
            </>
          ) : (
            <>
              <Button type="primary" icon={<SaveOutlined />} onClick={save}>存檔</Button>
              <Button icon={<CloseOutlined />} onClick={cancel}>還原</Button>
            </>
          )}
        </div>
      </div>
    </Spin>
  );
};

export default CUSForm;
